// The value of a column filter and a sort, as the portal's lists keep them in the address (components/ColumnHead.tsx).
// A paged list sends them to the API, which reads them with services/api/src/lib/columnFilters.ts; a list held in the
// browser, such as Magazijnorders, applies them itself with the tests at the end.

export interface SortState {
  sort?: string;
  order?: "asc" | "desc";
}

/** "a..b" as its two sides; a single value is both. */
export function rangeOf(value: string | undefined): [string, string] {
  if (!value) return ["", ""];
  if (!value.includes("..")) return [value, value];
  const [from = "", to = ""] = value.split("..");
  return [from.trim(), to.trim()];
}

/** Two sides as one value; both empty is no filter. */
export function rangeValue(from: string, to: string): string {
  const a = from.trim();
  const b = to.trim();
  if (!a && !b) return "";
  if (a && a === b) return a;
  return `${a}..${b}`;
}

/** The chosen values of an options filter. */
export function optionsOf(value: string | undefined): string[] {
  return value
    ? value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
    : [];
}

/** The next sort after a click on a column: up, then down, then the list's own order again. */
export function nextSort(current: SortState, column: string): SortState {
  if (current.sort !== column) return { sort: column, order: "asc" };
  if (current.order !== "desc") return { sort: column, order: "desc" };
  return {};
}

/** Text compared without case or accents: "Düsseldorf" holds "dussel". */
export function folded(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Whether one of the texts holds the filter. */
export function textPasses(filter: string, texts: (string | null | undefined)[]): boolean {
  const needle = folded(filter.trim());
  return texts.some((text) => text != null && folded(text).includes(needle));
}

/** Whether a day (YYYY-MM-DD) lies within a range of days; either side may be open. */
export function dayPasses(filter: string, day: string | null | undefined): boolean {
  if (!day) return false;
  const [from, to] = rangeOf(filter);
  return (!from || day >= from) && (!to || day <= to);
}

/** Whether a value is one of the chosen. */
export function optionPasses(filter: string, value: string | null | undefined): boolean {
  return value != null && optionsOf(filter).includes(value);
}
